
import React, { useEffect } from 'react';
import { useAdminProtectedRoute } from '@/contexts/admin-context';
import { Loader2 } from 'lucide-react';
import AdminSidebar from './AdminSidebar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/components/ui/use-toast';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { loading, isAdmin } = useAdminProtectedRoute();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useEffect(() => {
    // If user isn't logged in at all, redirect to login
    if (!loading && !user) {
      toast({
        title: "Authentication Required",
        description: "Please login to continue.",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }
    
    // If user is logged in but not admin, redirect to home
    if (!loading && user && !isAdmin) {
      toast({
        title: "Access denied",
        description: "You don't have permission to access this area.",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [isAdmin, loading, navigate, toast, user]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg">Loading admin dashboard...</span>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // The useEffect will redirect
  }

  return (
    <div className="flex min-h-screen bg-gray-100 dark:bg-gray-900">
      <AdminSidebar />
      <div className="flex-1 p-8">
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
